import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { toast } from 'sonner';
import { safeFormatDate } from '../lib/dateTime';
import {
  Users,
  Plus,
  Edit2,
  Trash2,
  Mail,
  Shield,
  Search,
  UserCheck,
  UserX,
  Clock,
  Calendar,
  History,
  ChevronDown,
  ChevronUp,
  Key,
  MapPin,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { GoBDDeleteConfirmDialog, useGoBDDelete } from '../components/GoBDDeleteConfirmDialog';
import { AuditHistoryPanel } from '../components/AuditHistoryPanel';

export default function UsersPage() {
  const { api, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isHoursDialogOpen, setIsHoursDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [passwordChangeUser, setPasswordChangeUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [hoursChangeUser, setHoursChangeUser] = useState(null);
  const [hoursHistory, setHoursHistory] = useState([]);
  const [expandedUser, setExpandedUser] = useState(null);

  // GoBD Delete Dialog hook
  const { 
    dialogOpen: gobdDeleteDialogOpen, 
    setDialogOpen: setGobdDeleteDialogOpen, 
    deleteConfig, 
    requestDelete, 
    handleConfirm: handleGobdDeleteConfirm, 
    handleCancel: handleGobdDeleteCancel 
  } = useGoBDDelete();
  
  // Audit History state
  const [historyPanelOpen, setHistoryPanelOpen] = useState(false);
  const [historyEntry, setHistoryEntry] = useState({ collection: '', id: '' });
  
  const showHistory = (collection, id) => {
    setHistoryEntry({ collection, id });
    setHistoryPanelOpen(true);
  };

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role: 'employee',
    supervisor_id: '',
    weekly_hours: 40,
    vacation_days_per_year: 30,
    annual_vacation_days: '',
    statutory_vacation_days: '',
    employee_id: '',
    personnel_number: '',
    department: '',
    cost_center: '',
    phone: '',
    employment_start: '',
    employment_end: '',
    time_account_start: '',
    work_location: '',
    state: '',  // Bundesland
  });

  const [hoursChangeData, setHoursChangeData] = useState({
    new_hours: 40,
    effective_date: format(new Date(), 'yyyy-MM-dd'),
    reason: '',
  });

  // Holiday states (Bundesländer)
  const [states, setStates] = useState([]);
  const [companyDefaultState, setCompanyDefaultState] = useState('NI');

  const supervisors = users.filter(u => u.role === 'supervisor' || u.role === 'admin');

  useEffect(() => {
    fetchUsers();
    fetchStates();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Fehler beim Laden der Benutzer');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStates = async () => {
    try {
      const [statesRes, settingsRes] = await Promise.all([
        api.get('/holidays/states'),
        api.get('/holidays/settings').catch(() => ({ data: null })),
      ]);
      setStates(statesRes.data);
      if (settingsRes.data?.default_state) {
        setCompanyDefaultState(settingsRes.data.default_state);
      }
    } catch (error) {
      console.error('Failed to fetch states:', error);
    }
  };

  const getStateName = (code) => {
    const state = states.find(s => s.code === code);
    return state?.name || code;
  };

  const fetchHoursHistory = async (userId) => {
    try {
      const response = await api.get(`/users/${userId}/employment-terms`);
      setHoursHistory(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch hours history:', error);
      setHoursHistory([]);
      toast.error('Historie konnte nicht geladen werden');
    }
  };

  const todayStr = format(new Date(), 'yyyy-MM-dd');
  const sortedHistory = [...hoursHistory].sort((a, b) => (a.valid_from < b.valid_from ? 1 : -1));
  const currentTermId = sortedHistory.find((t) => t.valid_from <= todayStr)?.id;
  const oldestTermId = sortedHistory.length ? sortedHistory[sortedHistory.length - 1].id : null;

  const handleDeleteTerm = async (userId, termId) => {
    if (!window.confirm('Geplante Änderung wirklich löschen?')) return;
    try {
      await api.delete(`/users/${userId}/employment-terms/${termId}`);
      toast.success('Geplante Änderung gelöscht');
      await fetchHoursHistory(userId);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const renderTermRow = (term, { userId, allowDelete }) => {
    const isPlanned = term.valid_from > todayStr;
    const isCurrent = term.id === currentTermId;
    const canDelete = allowDelete && isPlanned && currentUser?.role === 'admin' && term.id !== oldestTermId;
    return (
      <div
        key={term.id}
        className={`flex items-start justify-between gap-3 p-3 rounded-lg bg-muted/50 ${!isPlanned && !isCurrent ? 'text-muted-foreground' : ''}`}
        data-testid={`employment-term-${term.id}`}
      >
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`text-lg font-mono font-bold ${isPlanned || isCurrent ? 'text-foreground' : 'text-muted-foreground'}`}>
              {term.weekly_hours} h/Woche
            </span>
            <span className="text-sm text-muted-foreground">ab {safeFormatDate(term.valid_from, 'd.M.yyyy')}</span>
            {isPlanned && (
              <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-500/15 text-blue-700 dark:text-blue-300 border border-blue-500/30">geplant</span>
            )}
            {isCurrent && (
              <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30">aktuell</span>
            )}
          </div>
          {(term.note || term.created_by_name) && (
            <p className="text-xs text-muted-foreground mt-0.5">
              {term.note}{term.note && term.created_by_name ? ' · ' : ''}{term.created_by_name}
            </p>
          )}
        </div>
        {canDelete && (
          <button
            type="button"
            onClick={() => handleDeleteTerm(userId, term.id)}
            className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-accent"
            title="Geplante Änderung löschen"
            data-testid={`delete-term-${term.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    );
  };

  const openCreateDialog = () => {
    setEditingUser(null);
    setFormData({
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      role: 'employee',
      supervisor_id: '',
      weekly_hours: 40,
      vacation_days_per_year: 30,
      annual_vacation_days: '',
      statutory_vacation_days: '',
      employee_id: '',
      personnel_number: '',
      department: '',
      cost_center: '',
      phone: '',
      employment_start: '',
      employment_end: '',
      time_account_start: '',
      work_location: '',
      state: '',
    });
    setIsDialogOpen(true);
  };

  const openEditDialog = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      password: '',
      first_name: user.first_name,
      last_name: user.last_name,
      role: user.role,
      supervisor_id: user.supervisor_id || '',
      weekly_hours: user.weekly_hours || 40,
      vacation_days_per_year: user.vacation_days_per_year || 30,
      annual_vacation_days: user.annual_vacation_days || '',
      statutory_vacation_days: user.statutory_vacation_days || '',
      employee_id: user.employee_id || '',
      personnel_number: user.personnel_number || '',
      department: user.department || '',
      cost_center: user.cost_center || '',
      phone: user.phone || '',
      employment_start: user.employment_start || '',
      employment_end: user.employment_end || '',
      time_account_start: user.time_account_start || '',
      work_location: user.work_location || '',
      state: user.state || '',
    });
    setIsDialogOpen(true);
  };

  const openHoursChangeDialog = (user) => {
    setHoursChangeUser(user);
    setHoursChangeData({
      new_hours: user.weekly_hours || 40,
      effective_date: format(new Date(), 'yyyy-MM-dd'),
      reason: '',
    });
    fetchHoursHistory(user.id);
    setIsHoursDialogOpen(true);
  };

  const toggleUserExpand = async (userId) => {
    if (expandedUser === userId) {
      setExpandedUser(null);
    } else {
      setExpandedUser(userId);
      await fetchHoursHistory(userId);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const payload = { ...formData, time_account_start: formData.time_account_start || null };
      if (editingUser) {
        const updateData = { ...payload };
        delete updateData.password;
        delete updateData.email;
        await api.put(`/users/${editingUser.id}`, updateData);
        toast.success('Benutzer aktualisiert');
      } else {
        await api.post('/admin/users', payload);
        toast.success('Benutzer erstellt');
      }

      setIsDialogOpen(false);
      fetchUsers();
    } catch (error) {
      let message = 'Fehler beim Speichern';
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        message = detail;
      } else if (Array.isArray(detail)) {
        // Pydantic validation errors
        message = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      } else if (detail && typeof detail === 'object') {
        message = detail.msg || detail.message || JSON.stringify(detail);
      }
      toast.error(message);
    }
  };

  const handleHoursChangeSubmit = async (e) => {
    e.preventDefault();

    if (!hoursChangeData.reason.trim()) {
      toast.error('Bitte geben Sie einen Grund an');
      return;
    }

    try {
      await api.post(`/users/${hoursChangeUser.id}/employment-terms`, {
        valid_from: hoursChangeData.effective_date,
        weekly_hours: hoursChangeData.new_hours,
        annual_vacation_days: null,
        note: hoursChangeData.reason,
      });
      toast.success('Wochenstunden-Änderung gespeichert');
      setIsHoursDialogOpen(false);
      fetchUsers();
      fetchHoursHistory(hoursChangeUser.id);
    } catch (error) {
      const message = error.response?.data?.detail || 'Fehler beim Speichern';
      toast.error(message);
    }
  };

  const handleDeactivate = async (userId) => {
    if (userId === currentUser.id) {
      toast.error('Sie können sich nicht selbst deaktivieren');
      return;
    }
    
    // Open confirmation dialog
    const userToDeactivate = users.find(u => u.id === userId);
    setUserToDelete(userToDeactivate);
    setIsDeleteDialogOpen(true);
  };

  const confirmDeactivate = async () => {
    if (!userToDelete) return;
    
    try {
      await api.delete(`/users/${userToDelete.id}`);
      toast.success('Benutzer deaktiviert');
      setIsDeleteDialogOpen(false);
      setUserToDelete(null);
      fetchUsers();
    } catch (error) {
      const message = error.response?.data?.detail || 'Fehler beim Deaktivieren';
      toast.error(message);
    }
  };

  const handleReactivate = async (userId) => {
    try {
      await api.put(`/users/${userId}`, { is_active: true });
      toast.success('Benutzer reaktiviert');
      fetchUsers();
    } catch (error) {
      const message = error.response?.data?.detail || 'Fehler beim Reaktivieren';
      toast.error(message);
    }
  };

  const openPasswordDialog = (user) => {
    setPasswordChangeUser(user);
    setNewPassword('');
    setIsPasswordDialogOpen(true);
  };

  const handlePasswordChange = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Passwort muss mindestens 6 Zeichen lang sein');
      return;
    }

    try {
      await api.post(`/admin/users/${passwordChangeUser.id}/password`, {
        new_password: newPassword,
      });
      toast.success(`Passwort für ${passwordChangeUser.first_name} ${passwordChangeUser.last_name} geändert`);
      setIsPasswordDialogOpen(false);
      setPasswordChangeUser(null);
      setNewPassword('');
    } catch (error) {
      const message = error.response?.data?.detail || 'Fehler beim Ändern des Passworts';
      toast.error(message);
    }
  };

  const getRoleBadge = (role) => {
    const styles = {
      admin: 'bg-red-500/20 text-red-400 border-red-500/30',
      supervisor: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      accounting: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
      employee: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    };
    const labels = {
      admin: 'Admin',
      supervisor: 'Vorgesetzter',
      accounting: 'Buchhaltung',
      employee: 'Mitarbeiter',
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${styles[role]}`}>
        {labels[role]}
      </span>
    );
  };

  const filteredUsers = users.filter(
    (user) => {
      // Filter by search query
      const matchesSearch = 
        user.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.last_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchQuery.toLowerCase());
      
      // Filter by active status
      const matchesActiveFilter = showInactive || user.is_active !== false;
      
      return matchesSearch && matchesActiveFilter;
    }
  );

  const activeCount = users.filter(u => u.is_active !== false).length;
  const inactiveCount = users.filter(u => u.is_active === false).length;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="users-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground">Benutzer</h1>
          <p className="text-muted-foreground mt-1">
            {activeCount} aktiv{inactiveCount > 0 && <span className="text-muted-foreground"> · {inactiveCount} deaktiviert</span>}
          </p>
        </div>

        <Button
          onClick={openCreateDialog}
          data-testid="create-user-button"
          className="bg-indigo-500 hover:bg-indigo-600 text-white glow-primary"
        >
          <Plus className="w-4 h-4 mr-2" />
          Neuer Benutzer
        </Button>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            type="text"
            data-testid="search-users"
            placeholder="Benutzer suchen..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-input border-border text-foreground"
          />
        </div>
        
        {inactiveCount > 0 && (
          <Button
            variant="outline"
            onClick={() => setShowInactive(!showInactive)}
            className={`border-border ${showInactive ? 'text-amber-600 dark:text-amber-400 border-amber-500/30' : 'text-muted-foreground'}`}
          >
            <UserX className="w-4 h-4 mr-2" />
            {showInactive ? 'Deaktivierte ausblenden' : `Deaktivierte anzeigen (${inactiveCount})`}
          </Button>
        )}
      </div>

      {/* Users List */}
      <div className="space-y-4">
        {filteredUsers.map((user) => (
          <motion.div
            key={user.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`card-glass overflow-hidden ${user.is_active === false ? 'opacity-60' : ''}`}
          >
            {/* User Row */}
            <div className="p-4 md:p-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                {/* User Info */}
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${user.is_active === false ? 'bg-muted/50' : 'bg-indigo-500/20'}`}>
                    <span className={`font-medium ${user.is_active === false ? 'text-muted-foreground' : 'text-indigo-600 dark:text-indigo-400'}`}>
                      {user.first_name?.[0]}{user.last_name?.[0]}
                    </span>
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className={`font-medium ${user.is_active === false ? 'text-muted-foreground' : 'text-foreground'}`}>
                        {user.first_name} {user.last_name}
                      </p>
                      {user.is_active === false && (
                        <span className="px-2 py-0.5 text-xs rounded-full bg-muted text-muted-foreground border border-border">
                          Deaktiviert
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">{user.email}</p>
                  </div>
                </div>

                {/* Quick Info */}
                <div className="flex flex-wrap items-center gap-4 md:gap-6">
                  {getRoleBadge(user.role)}
                  
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-muted-foreground" />
                    <span className="text-foreground font-mono">{user.weekly_hours || 40}h/Woche</span>
                  </div>

                  {user.supervisor_id && (
                    <span className="text-sm text-muted-foreground">
                      Vorgesetzter: {users.find(u => u.id === user.supervisor_id)?.first_name || '-'}
                    </span>
                  )}

                  <div className="flex items-center gap-2">
                    {user.is_active !== false && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openHoursChangeDialog(user)}
                          data-testid={`change-hours-${user.id}`}
                          className="border-indigo-500/30 text-indigo-400 hover:bg-indigo-500/10"
                        >
                          <Clock className="w-4 h-4 mr-1" />
                          Stunden
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openPasswordDialog(user)}
                          data-testid={`change-password-${user.id}`}
                          className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                          title="Passwort ändern"
                        >
                          <Key className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditDialog(user)}
                          className="border-border text-muted-foreground hover:text-foreground"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                    {user.id !== currentUser.id && user.is_active !== false && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeactivate(user.id)}
                        data-testid={`deactivate-user-${user.id}`}
                        className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                        title="Benutzer deaktivieren"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                    {user.is_active === false && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleReactivate(user.id)}
                        data-testid={`reactivate-user-${user.id}`}
                        className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                        title="Benutzer reaktivieren"
                      >
                        <UserCheck className="w-4 h-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleUserExpand(user.id)}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      {expandedUser === user.id ? (
                        <ChevronUp className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Expanded - Hours History */}
            {expandedUser === user.id && (
              <div className="border-t border-border p-4 md:p-6 bg-accent/20">
                <h4 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                  <History className="w-4 h-4" />
                  Wochenstunden-Verlauf
                </h4>
                {sortedHistory.length > 0 ? (
                  <div className="space-y-2">
                    {sortedHistory.map((term) => renderTermRow(term, { userId: user.id, allowDelete: false }))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Noch keine Einträge.</p>
                )}
              </div>
            )}
          </motion.div>
        ))}
      </div>

      {/* Empty State */}
      {filteredUsers.length === 0 && (
        <div className="text-center py-16">
          <Users className="w-12 h-12 text-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">Keine Benutzer gefunden</h3>
          <p className="text-muted-foreground">
            {searchQuery ? 'Versuchen Sie eine andere Suche' : 'Erstellen Sie den ersten Benutzer'}
          </p>
        </div>
      )}

      {/* Create/Edit User Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-card border-border max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-foreground">
              {editingUser ? 'Benutzer bearbeiten' : 'Neuer Benutzer'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-muted-foreground">Vorname</Label>
                <Input
                  data-testid="user-first-name"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  required
                  className="bg-input border-border text-foreground"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground">Nachname</Label>
                <Input
                  data-testid="user-last-name"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  required
                  className="bg-input border-border text-foreground"
                />
              </div>
            </div>

            {!editingUser && (
              <>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">E-Mail</Label>
                  <Input
                    data-testid="user-email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="bg-input border-border text-foreground"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-muted-foreground">Passwort</Label>
                  <Input
                    data-testid="user-password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    className="bg-input border-border text-foreground"
                  />
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label className="text-muted-foreground">Rolle</Label>
              <Select
                value={formData.role}
                onValueChange={(value) => setFormData({ ...formData, role: value })}
              >
                <SelectTrigger className="bg-input border-border text-foreground">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="employee">Mitarbeiter</SelectItem>
                  <SelectItem value="supervisor">Vorgesetzter</SelectItem>
                  <SelectItem value="accounting">Buchhaltung</SelectItem>
                  <SelectItem value="admin">Administrator</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-muted-foreground">Vorgesetzter</Label>
              <Select
                value={formData.supervisor_id || 'none'}
                onValueChange={(value) => setFormData({ ...formData, supervisor_id: value === 'none' ? '' : value })}
              >
                <SelectTrigger className="bg-input border-border text-foreground">
                  <SelectValue placeholder="Keiner" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="none">Keiner</SelectItem>
                  {supervisors.map((sup) => (
                    <SelectItem key={sup.id} value={sup.id}>
                      {sup.first_name} {sup.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-muted-foreground">Wochenstunden</Label>
                <Input
                  type="number"
                  min="0"
                  max="60"
                  step="0.5"
                  value={formData.weekly_hours}
                  onChange={(e) => setFormData({ ...formData, weekly_hours: parseFloat(e.target.value) })}
                  required
                  disabled={!!editingUser}
                  className={`bg-input border-border text-foreground ${editingUser ? 'opacity-60 cursor-not-allowed' : ''}`}
                  data-testid="user-weekly-hours-input"
                />
                {editingUser && (
                  <p className="text-xs text-muted-foreground">Änderung nur über „Wochenstunden ändern“ mit Gültigkeitsdatum.</p>
                )}
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground">Urlaubstage/Jahr</Label>
                <Input
                  type="number"
                  min="0"
                  max="50"
                  value={formData.vacation_days_per_year}
                  onChange={(e) => setFormData({ ...formData, vacation_days_per_year: parseInt(e.target.value) })}
                  required
                  className="bg-input border-border text-foreground"
                />
              </div>
            </div>

            {/* Vertragsaufteilung Urlaub */}
            <div className="border-t border-border pt-4 mt-4">
              <p className="text-sm font-medium text-muted-foreground mb-2">Vertragsaufteilung Urlaub</p>
              <p className="text-xs text-muted-foreground mb-3">
                Altvertrag: 30 gesamt, 30 gesetzlich → alle Tage folgen der gesetzlichen Regelung.
                Neuvertrag: 30 gesamt, 20 gesetzlich → 10 vertragliche Tage werden anteilig gerechnet.
                Leer lassen = Firmenvorgabe.
              </p>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Gesamturlaub (Tage)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="50"
                    step="0.5"
                    value={formData.annual_vacation_days}
                    onChange={(e) => setFormData({ ...formData, annual_vacation_days: e.target.value ? parseFloat(e.target.value) : '' })}
                    placeholder="z.B. 30"
                    className="bg-input border-border text-foreground"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">davon gesetzlich (Tage)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="50"
                    step="0.5"
                    value={formData.statutory_vacation_days}
                    onChange={(e) => setFormData({ ...formData, statutory_vacation_days: e.target.value ? parseFloat(e.target.value) : '' })}
                    placeholder="z.B. 20"
                    className="bg-input border-border text-foreground"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Vertraglicher Anteil</Label>
                  <div className="px-3 py-2 bg-card/50 border border-border rounded-md text-muted-foreground text-sm">
                    {formData.annual_vacation_days && formData.statutory_vacation_days 
                      ? `${Math.max(0, formData.annual_vacation_days - formData.statutory_vacation_days).toFixed(1)} Tage`
                      : formData.annual_vacation_days 
                        ? `${Math.max(0, formData.annual_vacation_days - 20).toFixed(1)} Tage (Std.)`
                        : '—'
                    }
                  </div>
                </div>
              </div>
            </div>

            {/* Additional User Fields */}
            <div className="border-t border-border pt-4 mt-4">
              <p className="text-sm font-medium text-muted-foreground mb-3">Stammdaten</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Personalnummer (DATEV)</Label>
                  <Input
                    value={formData.personnel_number}
                    onChange={(e) => setFormData({ ...formData, personnel_number: e.target.value })}
                    placeholder="z.B. 1001"
                    className="bg-input border-border text-foreground"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Mitarbeiter-ID</Label>
                  <Input
                    value={formData.employee_id}
                    onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                    placeholder="z.B. MA-001"
                    className="bg-input border-border text-foreground"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mt-3">
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Abteilung</Label>
                  <Input
                    value={formData.department}
                    onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                    placeholder="z.B. Entwicklung"
                    className="bg-input border-border text-foreground"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Kostenstelle</Label>
                  <Input
                    value={formData.cost_center}
                    onChange={(e) => setFormData({ ...formData, cost_center: e.target.value })}
                    placeholder="z.B. 1000"
                    className="bg-input border-border text-foreground"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mt-3">
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Telefon</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="z.B. +49 123 456789"
                    className="bg-input border-border text-foreground"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Arbeitsort</Label>
                  <Input
                    value={formData.work_location}
                    onChange={(e) => setFormData({ ...formData, work_location: e.target.value })}
                    placeholder="z.B. Hamburg"
                    className="bg-input border-border text-foreground"
                  />
                </div>
              </div>
              
              <p className="text-sm font-medium text-muted-foreground mb-3 mt-4">Beschäftigung</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Beschäftigungsbeginn</Label>
                  <Input
                    type="date"
                    value={formData.employment_start}
                    onChange={(e) => setFormData({ ...formData, employment_start: e.target.value })}
                    className="bg-input border-border text-foreground"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Beschäftigungsende</Label>
                  <Input
                    type="date"
                    value={formData.employment_end}
                    onChange={(e) => setFormData({ ...formData, employment_end: e.target.value })}
                    className="bg-input border-border text-foreground"
                  />
                </div>
              </div>
              <div className="space-y-2 mt-4">
                <Label className="text-muted-foreground">Arbeitszeitkonto geführt ab</Label>
                <Input
                  type="date"
                  value={formData.time_account_start}
                  onChange={(e) => setFormData({ ...formData, time_account_start: e.target.value })}
                  className="bg-input border-border text-foreground"
                  data-testid="time-account-start-input"
                />
                <p className="text-xs text-muted-foreground">
                  Erst ab diesem Datum rechnet das Arbeitszeitkonto. Leer lassen, wenn ab Jahresbeginn gerechnet werden soll. Nicht mit dem Eintrittsdatum verwechseln – das bleibt für die Urlaubsberechnung maßgeblich.
                </p>
              </div>
              
              <p className="text-sm font-medium text-muted-foreground mb-3 mt-4">Feiertagsberechnung</p>
              <div className="space-y-2">
                <Label className="text-muted-foreground">Bundesland (Arbeitsort)</Label>
                <Select
                  value={formData.state || 'company'}
                  onValueChange={(v) => setFormData({ ...formData, state: v === 'company' ? '' : v })}
                >
                  <SelectTrigger className="bg-input border-border text-foreground">
                    <SelectValue placeholder="Wie Unternehmen" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border max-h-[200px]">
                    <SelectItem value="company">
                      Wie Unternehmen ({getStateName(companyDefaultState)})
                    </SelectItem>
                    {states.map((state) => (
                      <SelectItem key={state.code} value={state.code}>
                        {state.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Maßgeblich ist der Ort der Arbeitsleistung, nicht der Wohnort. 
                  Nur ausfüllen, wenn der Mitarbeiter dauerhaft in einem anderen Bundesland arbeitet.
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="border-border text-muted-foreground">
                Abbrechen
              </Button>
              <Button type="submit" className="bg-indigo-500 hover:bg-indigo-600 text-foreground">
                {editingUser ? 'Speichern' : 'Erstellen'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Weekly Hours Change Dialog */}
      <Dialog open={isHoursDialogOpen} onOpenChange={setIsHoursDialogOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-foreground">
              Wochenstunden ändern: {hoursChangeUser?.first_name} {hoursChangeUser?.last_name}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleHoursChangeSubmit} className="space-y-4">
            <div className="p-4 rounded-lg bg-muted/50">
              <p className="text-sm text-muted-foreground">Aktuelle Wochenstunden</p>
              <p className="text-2xl font-mono font-bold text-foreground">{hoursChangeUser?.weekly_hours || 40}h</p>
            </div>

            <div className="space-y-2">
              <Label className="text-muted-foreground">Neue Wochenstunden</Label>
              <Input
                type="number"
                min="0"
                max="60"
                step="0.5"
                value={hoursChangeData.new_hours}
                onChange={(e) => setHoursChangeData({ ...hoursChangeData, new_hours: parseFloat(e.target.value) })}
                required
                className="bg-input border-border text-foreground text-lg"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-muted-foreground">Gültig ab</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start bg-input border-border text-foreground hover:bg-card"
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    {safeFormatDate(hoursChangeData.effective_date, 'd. MMMM yyyy')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-card border-border" align="start">
                  <CalendarComponent
                    mode="single"
                    selected={new Date(hoursChangeData.effective_date)}
                    onSelect={(date) => setHoursChangeData({ ...hoursChangeData, effective_date: format(date, 'yyyy-MM-dd') })}
                    locale={de}
                  />
                </PopoverContent>
              </Popover>
              {hoursChangeData.effective_date < todayStr && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30" data-testid="retroactive-warning">
                  <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                  <p className="text-sm text-amber-800 dark:text-amber-300">
                    Rückwirkende Änderung: Das Arbeitszeitkonto wird ab diesem Datum neu berechnet. Bereits genehmigte Wochen behalten ihre erfassten Zeiten, aber die Sollstunden und damit der Saldo ändern sich.
                  </p>
                </div>
              )}
              {hoursChangeData.effective_date > todayStr && (
                <p className="text-xs text-muted-foreground" data-testid="future-hint">Änderung wird erst ab diesem Datum wirksam.</p>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-muted-foreground">Grund der Änderung *</Label>
              <Textarea
                value={hoursChangeData.reason}
                onChange={(e) => setHoursChangeData({ ...hoursChangeData, reason: e.target.value })}
                placeholder="z.B. Wechsel von Vollzeit in Teilzeit"
                required
                className="bg-input border-border text-foreground min-h-[80px]"
              />
            </div>

            {/* Historie */}
            <div className="space-y-2">
              <Label className="text-muted-foreground flex items-center gap-2">
                <History className="w-4 h-4" />
                Bisherige Einträge
              </Label>
              {sortedHistory.length > 0 ? (
                <div className="max-h-48 overflow-y-auto space-y-2" data-testid="employment-terms-list">
                  {sortedHistory.map((term) => renderTermRow(term, { userId: hoursChangeUser?.id, allowDelete: true }))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Noch keine Einträge.</p>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsHoursDialogOpen(false)} className="border-border text-muted-foreground">
                Abbrechen
              </Button>
              <Button type="submit" className="bg-indigo-500 hover:bg-indigo-600 text-foreground">
                Änderung speichern
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-foreground font-heading flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-600 dark:text-red-400" />
              Benutzer deaktivieren
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-muted-foreground">
              Möchten Sie den Benutzer <span className="font-medium text-foreground">{userToDelete?.first_name} {userToDelete?.last_name}</span> wirklich deaktivieren?
            </p>
            
            <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <p className="text-sm text-amber-600 dark:text-amber-400">
                Der Benutzer kann sich nicht mehr anmelden, aber seine Daten (Zeiteinträge, etc.) bleiben erhalten und können später reaktiviert werden.
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
              className="border-border text-muted-foreground"
            >
              Abbrechen
            </Button>
            <Button
              onClick={confirmDeactivate}
              data-testid="confirm-deactivate-button"
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Deaktivieren
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password Change Dialog */}
      <Dialog open={isPasswordDialogOpen} onOpenChange={setIsPasswordDialogOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-foreground font-heading flex items-center gap-2">
              <Key className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              Passwort ändern
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-muted-foreground">
              Neues Passwort für <span className="font-medium text-foreground">{passwordChangeUser?.first_name} {passwordChangeUser?.last_name}</span>
            </p>
            
            <div className="space-y-2">
              <Label className="text-muted-foreground">Neues Passwort</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Mindestens 6 Zeichen"
                data-testid="new-password-input"
                className="bg-input border-border text-foreground"
              />
            </div>

            <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <p className="text-sm text-amber-600 dark:text-amber-400">
                Der Benutzer wird bei der nächsten Anmeldung das neue Passwort verwenden müssen.
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsPasswordDialogOpen(false)}
              className="border-border text-muted-foreground"
            >
              Abbrechen
            </Button>
            <Button
              onClick={handlePasswordChange}
              data-testid="confirm-password-change-button"
              className="bg-amber-500 hover:bg-amber-600 text-white"
            >
              <Key className="w-4 h-4 mr-2" />
              Passwort ändern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* GoBD Delete Confirmation Dialog */}
      <GoBDDeleteConfirmDialog
        open={gobdDeleteDialogOpen}
        onOpenChange={setGobdDeleteDialogOpen}
        title={deleteConfig.title}
        itemDescription={deleteConfig.itemDescription}
        requireReason={deleteConfig.requireReason}
        onConfirm={handleGobdDeleteConfirm}
        onCancel={handleGobdDeleteCancel}
      />

      {/* Audit History Panel Dialog */}
      <Dialog open={historyPanelOpen} onOpenChange={setHistoryPanelOpen}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <History className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              Änderungshistorie
            </DialogTitle>
          </DialogHeader>
          {historyEntry.id && (
            <AuditHistoryPanel
              collectionName={historyEntry.collection}
              documentId={historyEntry.id}
              isOpen={true}
              compact={false}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
